import React, { useState } from "react";
import { Helmet } from "react-helmet";
import { toast } from "react-toastify";
import AakashgangaHeader from "../components/global/HeaderComponent";
import AakashgangaFooter from "../components/global/FooterComponent";
import SubHeader from "../components/global/SubHeader";
import Content from "../components/global/Content";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";

function MegaJournalFormattingGuideline() {
  const [isMegaGuidelineAgreed, setIsMegaGuidelineAgreed] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  const handleContinue = () => {
    if (!isMegaGuidelineAgreed) {
      toast.error("Please check the box to agree to the formatting guidelines.");
      return;
    }
    localStorage.setItem("megaGuidelineAgreed", "true");
    localStorage.setItem("megaGuidelineAgreed_trigger", Date.now().toString());
    window.close();
    toast.success("Guidelines agreed. You can close this tab and return to your submission.");
  };

  const handleDownloadPdf = () => {
    const input = document.getElementById("pdf-content");
    if (!input) return;

    setIsDownloading(true);
    html2canvas(input, { scale: 2, useCORS: true }).then((canvas) => {
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const imgHeight = (canvas.height * pdfWidth) / canvas.width;
      let heightLeft = imgHeight;
      let position = 0;

      pdf.addImage(imgData, "PNG", 0, position, pdfWidth, imgHeight);
      heightLeft -= pdf.internal.pageSize.getHeight();

      while (heightLeft > 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, "PNG", 0, position, pdfWidth, imgHeight);
        heightLeft -= pdf.internal.pageSize.getHeight();
      }

      pdf.save("Mega-Journal-Formatting-Guideline.pdf");
      setIsDownloading(false);
    }).catch((err) => {
      console.error("Failed to generate PDF", err);
      toast.error("Failed to generate PDF.");
      setIsDownloading(false);
    });
  };

  return (
    <>
      <Helmet>
        <title>Aakashganga Open: Mega Journal Formatting Guideline</title>
        <meta
          name="description"
          content="Review the formatting requirements for submitting your article to a Mega Journal on Aakashganga Open."
        />
        <meta
          property="og:title"
          content="Aakashganga Open: Mega Journal Formatting Guideline"
          data-react-helmet="true"
        />
        <meta
          property="og:description"
          content="Review the formatting requirements for submitting your article to a Mega Journal on Aakashganga Open."
          data-react-helmet="true"
        />
      </Helmet>

      <div className="tagbody">
        <AakashgangaHeader />
        <h1 className="seo-related-tag">Mega Journal Formatting Guideline</h1>
        <main className="main">
          <SubHeader title={"Mega Journal Formatt<span>i</span>ng Guideline"} />

          <div className="content-area">
            <div className="container">
              <div id="pdf-content" style={{ backgroundColor: "#fff" }}>
                <div className="guideLineManu">

                  {/* SCOPE AND PURPOSE */}
                  <Content title="I. Scope and Purpose">
                    <p className="pera-text tagp">
                      These guidelines are intended to assist authors in preparing manuscripts that meet the scholarly and formatting standards required for submission to peer-reviewed journals and academic publishing platforms. The structure and style described herein are aligned with the conventions adopted expectations of Indian universities, the University Grants Commission (UGC), and internationally recognised indexing systems.
                    </p>
                    <p className="pera-text tagp">
                      The objective of these guidelines is to ensure that submitted research articles are:
                    </p>
                    <ul className="pera-text tagp" style={{ paddingLeft: "20px", marginBottom: "15px" }}>
                      <li style={{ marginBottom: "8px" }}>Academically rigorous and original</li>
                      <li style={{ marginBottom: "8px" }}>Clearly structured and logically sequenced</li>
                      <li style={{ marginBottom: "8px" }}>Easy to review and evaluate by independent reviewers</li>
                      <li style={{ marginBottom: "8px" }}>Compatible with peer-review workflows</li>
                      <li style={{ marginBottom: "8px" }}>Consistent with accepted indexing and archival standards</li>
                    </ul>
                    <p className="pera-text tagp">
                      <span className="red-text bold600">Submission format:</span> Submit document in PDF. If for some reason a PDF is not available then you can submit a .doc or .docx file. All other formats will be rejected by the system.
                    </p>
                  </Content>

                  {/* FORMATTING AND TYPOGRAPHY */}
                  <Content title="II. Formatting and Typography Specifications">
                    <h3 className="large-title tagh2" style={{ fontSize: "28px", marginTop: "15px" }}>A. Page Layout</h3>
                    <p className="pera-text tagp">
                      Manuscripts must be prepared on A4-sized (210 mm × 297 mm). Text should be formatted in a single-column layout. All text must be fully justified.
                    </p>

                    <h3 className="large-title tagh2" style={{ fontSize: "28px", marginTop: "15px" }}>B. Typeface and Font Sizes</h3>
                    <p className="pera-text tagp">
                      The prescribed typeface for all text is Times New Roman. The following font sizes apply:
                    </p>
                    <div className="table-responsive mb-3">
                      <table className="table table-bordered" style={{ fontSize: "14px" }}>
                        <thead style={{ backgroundColor: "#f0f8ff" }}>
                          <tr>
                            <th>Element</th>
                            <th>Specification</th>
                            <th>Preferred</th>
                            <th>Mandatory</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr><td>Page Size</td><td>A4 / Letter</td><td>A4</td><td>Yes</td></tr>
                          <tr><td>Body Font</td><td>Times New Roman</td><td>11 pt</td><td>Yes</td></tr>
                          <tr><td>Title Font</td><td>Times New Roman</td><td>14 pt Bold</td><td>Yes</td></tr>
                          <tr><td>Section Header 1</td><td>Times New Roman</td><td>13 Regular</td><td>Yes</td></tr>
                          <tr><td>Section Header 2</td><td>Times New Roman</td><td>12 Regular</td><td>Yes</td></tr>
                          <tr><td>Line Spacing</td><td>Single</td><td>Single</td><td>Yes</td></tr>
                          <tr><td>Abstract Length</td><td>150–400 words</td><td>300 words</td><td>Yes</td></tr>
                          <tr><td>Keywords</td><td>4–6 terms</td><td>5 terms</td><td>Yes</td></tr>
                          <tr><td>Reference Style</td><td>APA / MLA</td><td>MLA</td><td>Per journal</td></tr>
                        </tbody>
                      </table>
                    </div>
                    <p className="pera-text tagp">
                      <span className="red-text bold600">Note:</span> Only ONE reference style to be used. If multiple styles are found, the paper acceptance can be rejected.
                    </p>

                    <h3 className="large-title tagh2" style={{ fontSize: "18px", marginTop: "15px" }}>D. Spacing and Indentation</h3>
                    <ul className="pera-text tagp" style={{ paddingLeft: "20px", marginBottom: "15px" }}>
                      <li style={{ marginBottom: "8px" }}>Body text: single line spacing</li>
                      <li style={{ marginBottom: "8px" }}>First paragraph of each section: no indent</li>
                      <li style={{ marginBottom: "8px" }}>Spacing between sections: one blank line</li>
                    </ul>

                    <h3 className="large-title tagh2" style={{ fontSize: "18px", marginTop: "15px" }}>E. Page Numbering</h3>
                    <p className="pera-text tagp">
                      Pages must be numbered sequentially at the bottom centre of each page. Do not include page numbers in the manuscript header.
                    </p>
                  </Content>

                  {/* STRUCTURE OF THE RESEARCH ARTICLE */}
                  <Content title="III. Structure of the Research Article">
                    <p className="pera-text tagp">
                      A standard research article must adhere to the following prescribed structure. Optional sections are indicated. Authors must not reorder mandatory sections.
                    </p>

                    <h3 className="large-title tagh2" style={{ fontSize: "18px", marginTop: "20px" }}>A. Title of the Research Article</h3>
                    <p className="pera-text tagp" style={{ fontStyle: "italic", color: "#000" }}>Label Font: Times New Roman, Size 14 pt</p>
                    <p className="pera-text tagp">
                      The title is the most visible element of the article. It must communicate the central theme, subject area, and focus of the study with precision and clarity. Maximum 20 words.
                    </p>
                    <p className="pera-text tagp">A well-formed title typically incorporates the following elements:</p>
                    <ul className="pera-text tagp" style={{ paddingLeft: "20px", marginBottom: "30px" }}>
                      <li style={{ marginBottom: "8px" }}>Primary subject or phenomenon under investigation</li>
                      <li style={{ marginBottom: "8px" }}>Key variables or concepts examined</li>
                      <li style={{ marginBottom: "8px" }}>Target population or sample (where applicable)</li>
                      <li style={{ marginBottom: "8px" }}>Geographical or contextual scope</li>
                      <li style={{ marginBottom: "8px" }}>Methodology, where essential to the reader's understanding</li>
                    </ul>
                    <p className="pera-text tagp">
                      <span className="red-text bold600">Avoid:</span> Titles that are vague, overly broad, emotionally worded, or contain unexplained abbreviations. Remember titles generate URLs and URLs should be searchable.
                    </p>

                    <h3 className="large-title tagh2" style={{ fontSize: "18px", marginTop: "20px" }}>B. Abstract</h3>
                    <p className="pera-text tagp" style={{ fontStyle: "italic", color: "#000" }}>Label Font: Times New Roman, Size 14 pt | Body Text: 11 pt, Regular | Word limit: 150–400 words</p>
                    <p className="pera-text tagp">
                      The abstract provides a self-contained summary of the entire research article. It enables readers, reviewers, and indexing systems to rapidly assess the nature and relevance of the study.
                    </p>
                    <p className="pera-text tagp">A structured abstract must address each of the following components in sequence:</p>
                    <ol className="pera-text tagp" style={{ paddingLeft: "50px", marginBottom: "15px" }}>
                      <li style={{ marginBottom: "8px" }}><strong>Background / Context</strong> — Introduce the broader research problem.</li>
                      <li style={{ marginBottom: "8px" }}><strong>Objective</strong> — State the precise aim of the study.</li>
                      <li style={{ marginBottom: "8px" }}><strong>Methodology</strong> — Briefly describe the research approach and data collection method.</li>
                      <li style={{ marginBottom: "8px" }}><strong>Key Findings</strong> — Summarise the principal results.</li>
                      <li style={{ marginBottom: "8px" }}><strong>Conclusion / Implications</strong> — State the significance and potential impact of the findings.</li>
                    </ol>

                    <h3 className="large-title tagh2" style={{ fontSize: "18px", marginTop: "20px" }}>C. Keywords</h3>
                    <p className="pera-text tagp" style={{ fontStyle: "italic", color: "#000" }}>Label Font: Times New Roman, Size 14 pt</p>
                    <p className="pera-text tagp">
                      Immediately following the abstract, authors must provide a minimum of four (4) and a maximum of six (6) keywords. Keywords must be selected to maximise discoverability through academic search engines and indexing databases.
                    </p>
                    <p className="pera-text tagp">
                      Text Formatting: Label as "Keywords", followed by keywords in alphabetical order, separated by commas, in pt 11 Times New Roman.
                    </p>

                    <h3 className="large-title tagh2" style={{ fontSize: "18px", marginTop: "20px" }}>D. Introduction</h3>
                    <p className="pera-text tagp" style={{ fontStyle: "italic", color: "#000" }}>Label Font: Times New Roman, Size 14 pt | Body Text: 11 pt, Regular</p>
                    <p className="pera-text tagp">
                      The introduction establishes the intellectual foundation of the research study. It contextualises the problem within the broader academic field and prepares the reader for the study's contribution.
                    </p>
                    <p className="pera-text tagp">The introduction must include the following sub-components:</p>
                    <ol className="pera-text tagp" start="6" style={{ paddingLeft: "50px", marginBottom: "15px" }}>
                      <li style={{ marginBottom: "8px" }}><strong>Background of the Study</strong> — Contextual information introducing the field of inquiry.</li>
                      <li style={{ marginBottom: "8px" }}><strong>Statement of the Problem</strong> — A concise articulation of the research problem.</li>
                      <li style={{ marginBottom: "8px" }}><strong>Research Gap</strong> — Identification of limitations or unanswered questions in existing literature.</li>
                      <li style={{ marginBottom: "8px" }}><strong>Significance of the Study</strong> — Academic, social, industrial, or policy relevance.</li>
                      <li style={{ marginBottom: "8px" }}><strong>Research Objectives</strong> — Specific aims of the study, stated as declarative sentences.</li>
                      <li style={{ marginBottom: "8px" }}><strong>Research Questions or Hypotheses</strong> — Framed as questions (qualitative studies) or testable propositions (quantitative studies).</li>
                    </ol>
                    <p className="pera-text tagp">Recommended length: 10–20% of total article length.</p>

                    <h3 className="large-title tagh2" style={{ fontSize: "18px", marginTop: "20px" }}>E. Review of Literature (Optional)</h3>
                    <p className="pera-text tagp" style={{ fontStyle: "italic", color: "#000" }}>Font: Times New Roman, Size 14 pt | Body Text: 11 pt, Regular</p>
                    <p className="pera-text tagp">
                      The literature review situates the current study within the broader academic conversation. It demonstrates the author's command of prior research and justifies the need for the present investigation.
                    </p>
                    <p className="pera-text tagp">The review may be organised chronologically, thematically, conceptually, or by methodology. It must:</p>
                    <ul className="pera-text tagp" style={{ paddingLeft: "20px", marginBottom: "15px" }}>
                      <li style={{ marginBottom: "8px" }}>Summarise and critically analyse previous studies (not merely list them)</li>
                      <li style={{ marginBottom: "8px" }}>Identify trends, debates, and methodological variations</li>
                      <li style={{ marginBottom: "8px" }}>Highlight gaps in existing knowledge</li>
                      <li style={{ marginBottom: "8px" }}>Establish clear justification for the present study</li>
                    </ul>

                    <h3 className="large-title tagh2" style={{ fontSize: "18px", marginTop: "20px" }}>F. Research Methodology</h3>
                    <p className="pera-text tagp" style={{ fontStyle: "italic", color: "#000" }}>Label Font: Times New Roman, Size 14 pt | Body Text: 11 pt, Regular</p>
                    <p className="pera-text tagp">
                      The methodology section specifies how the research was designed and executed. It must be sufficiently detailed to allow independent replication of the study.
                    </p>
                    <p className="pera-text tagp">Authors must address each of the following sub-components:</p>
                    <ol className="pera-text tagp" start="12" style={{ paddingLeft: "50px", marginBottom: "15px" }}>
                      <li style={{ marginBottom: "8px" }}><strong>Research Design</strong> — e.g., descriptive, experimental, exploratory, case study, mixed methods.</li>
                      <li style={{ marginBottom: "8px" }}><strong>Research Approach</strong> — Qualitative, quantitative, or mixed methods.</li>
                      <li style={{ marginBottom: "8px" }}><strong>Population and Sample</strong> — Target population, sampling method, and sample size with rationale.</li>
                      <li style={{ marginBottom: "8px" }}><strong>Data Collection Methods</strong> — e.g., surveys, interviews, experiments, secondary databases.</li>
                      <li style={{ marginBottom: "8px" }}><strong>Research Instruments</strong> — e.g., structured questionnaire, interview schedule, standardised scales.</li>
                      <li style={{ marginBottom: "8px" }}><strong>Data Analysis Techniques</strong> — e.g., regression analysis, thematic analysis, ANOVA, SPSS-based analysis.</li>
                      <li style={{ marginBottom: "8px" }}><strong>Ethical Considerations</strong> — Informed consent, data confidentiality, institutional ethics approval.</li>
                    </ol>

                    <h3 className="large-title tagh2" style={{ fontSize: "18px", marginTop: "20px" }}>G. Results: Data Analysis and Interpretation</h3>
                    <p className="pera-text tagp" style={{ fontStyle: "italic", color: "#000" }}>Label Font: Times New Roman, Size 14 pt | Body Text: 11 pt, Regular</p>
                    <p className="pera-text tagp">
                      This section presents collected data and analytical findings. Data must be presented objectively, followed by interpretation that explains the meaning of the results in relation to research objectives and hypotheses.
                    </p>
                    <p className="pera-text tagp">Data may be presented through tables, graphs, charts, statistical outputs, or thematic summaries. All visual elements must be:</p>
                    <ul className="pera-text tagp" style={{ paddingLeft: "20px", marginBottom: "15px" }}>
                      <li style={{ marginBottom: "8px" }}>Sequentially numbered (Table 1, Table 2 … ; Figure 1, Figure 2 …)</li>
                      <li style={{ marginBottom: "8px" }}>Accompanied by a descriptive caption (above tables; below figures)</li>
                      <li style={{ marginBottom: "8px" }}>Referenced and discussed within the body text</li>
                      <li style={{ marginBottom: "8px" }}>Sourced where data originates from external publications</li>
                    </ul>

                    <h3 className="large-title tagh2" style={{ fontSize: "18px", marginTop: "20px" }}>H. Results: Findings of the Study</h3>
                    <p className="pera-text tagp" style={{ fontStyle: "italic", color: "#000" }}>Label Font: Times New Roman, Size 14 pt | Body Text: 11 pt, Regular</p>
                    <p className="pera-text tagp">
                      Findings summarise the most important outcomes derived from the analysis. Whereas the preceding sub-section explains the data, this section distils the conclusions emerging from that analysis.
                    </p>
                    <p className="pera-text tagp">Findings must be:</p>
                    <ul className="pera-text tagp" style={{ paddingLeft: "20px", marginBottom: "15px" }}>
                      <li style={{ marginBottom: "8px" }}>Clear, direct, and evidence-based</li>
                      <li style={{ marginBottom: "8px" }}>Explicitly linked to each stated research objective</li>
                      <li style={{ marginBottom: "8px" }}>Free from new data or analysis not previously presented</li>
                    </ul>

                    <h3 className="large-title tagh2" style={{ fontSize: "18px", marginTop: "20px" }}>I. Discussion</h3>
                    <p className="pera-text tagp" style={{ fontStyle: "italic", color: "#000" }}>Label Font: Times New Roman, Size 14 pt | Body Text: 11 pt, Regular</p>
                    <p className="pera-text tagp">
                      The discussion section contextualises findings within the broader academic literature and explains their implications. It is the section in which the author demonstrates scholarly depth and analytical maturity.
                    </p>
                    <p className="pera-text tagp">The discussion must include:</p>
                    <ol className="pera-text tagp" start="19" style={{ paddingLeft: "50px", marginBottom: "15px" }}>
                      <li style={{ marginBottom: "8px" }}><strong>Comparison with Existing Literature</strong> — Agreement and divergence with prior studies.</li>
                      <li style={{ marginBottom: "8px" }}><strong>Interpretation of Outcomes</strong> — Explanation of why certain findings emerged.</li>
                      <li style={{ marginBottom: "8px" }}><strong>Theoretical Implications</strong> — Contribution to academic theory or conceptual frameworks.</li>
                      <li style={{ marginBottom: "8px" }}><strong>Practical Implications</strong> — Relevance for industry, education, policy, or administration.</li>
                    </ol>

                    <h3 className="large-title tagh2" style={{ fontSize: "18px", marginTop: "20px" }}>J. Conclusion</h3>
                    <p className="pera-text tagp" style={{ fontStyle: "italic", color: "#000" }}>Font: Times New Roman, Size 14 pt | Body Text: 11 pt, Regular</p>
                    <p className="pera-text tagp">
                      The conclusion provides a concise, evidence-based closure to the study. It must summarise the research problem, key findings, major contributions, and overall implications without introducing new arguments or data.
                    </p>

                    <h3 className="large-title tagh2" style={{ fontSize: "18px", marginTop: "20px" }}>K. Recommendations (Optional)</h3>
                    <p className="pera-text tagp" style={{ fontStyle: "italic", color: "#000" }}>Label Font: Times New Roman, Size 14 pt | Body Text: 11 pt, Regular</p>
                    <p className="pera-text tagp">
                      Where appropriate, authors may include actionable recommendations for future researchers, policymakers, or practitioners. Recommendations must be realistic, specific, and directly connected to the study's findings.
                    </p>

                    <h3 className="large-title tagh2" style={{ fontSize: "18px", marginTop: "20px" }}>L. Limitations and Future Scope (Optional)</h3>
                    <p className="pera-text tagp" style={{ fontStyle: "italic", color: "#000" }}>Label Font: Times New Roman, Size 14 pt | Body Text: 11 pt, Regular</p>
                    <p className="pera-text tagp">
                      Authors are encouraged to acknowledge the constraints of their research (e.g., sample size, geographic scope, methodological limitations). A brief statement of potential directions for future research enhances transparency and academic credibility.
                    </p>

                    <h3 className="large-title tagh2" style={{ fontSize: "18px", marginTop: "20px" }}>M. References / Bibliography</h3>
                    <p className="pera-text tagp" style={{ fontStyle: "italic", color: "#000" }}>Label Font: Times New Roman, Size 14 pt | Body Text: 11 pt, Regular</p>
                    <p className="pera-text tagp">
                      All sources cited in the article must be listed in the references section. The reference list must be complete, consistent, and formatted according to the citation style specified by the journal.
                    </p>
                    <p className="pera-text tagp">We accept the following styles:</p>
                    <ul className="pera-text tagp" style={{ paddingLeft: "20px", marginBottom: "15px" }}>
                      <li style={{ marginBottom: "8px" }}>APA Style</li>
                      <li style={{ marginBottom: "8px" }}>MLA Style</li>
                      <li style={{ marginBottom: "8px" }}>Please include a DOI or URL of the article where possible.</li>
                    </ul>

                    <h3 className="large-title tagh2" style={{ fontSize: "18px", marginTop: "20px" }}>N. Appendices (Optional)</h3>
                    <p className="pera-text tagp" style={{ fontStyle: "italic", color: "#000" }}>Label Font: Times New Roman, Size 14 pt | Body Text: 12 pt, Regular</p>
                    <p className="pera-text tagp">
                      Supplementary material that supports but does not form part of the core argument may be appended. Examples include questionnaires, survey instruments, interview schedules, consent forms, raw data samples, or extended tables. All appendices must be labelled (Appendix A, Appendix B, etc.) and referenced within the article text.
                    </p>
                  </Content>

                  {/* VISUAL ELEMENTS */}
                  <Content title="IV. Visual Elements: Tables, Figures, and Images">
                    <p className="pera-text tagp">
                      Visual elements must supplement and clarify textual content. They must not duplicate information presented in prose form.
                    </p>

                    <h3 className="large-title tagh2" style={{ fontSize: "18px", marginTop: "15px" }}>A. Tables</h3>
                    <ul className="pera-text tagp" style={{ paddingLeft: "20px", marginBottom: "30px" }}>
                      <li style={{ marginBottom: "8px" }}>Number sequentially: Table I, Table II, Table III (Roman numerals per IEEE convention)</li>
                      <li style={{ marginBottom: "8px" }}>Place the title above the table in small capitals</li>
                      <li style={{ marginBottom: "8px" }}>Cite sources below the table where data is drawn from external works</li>
                      <li style={{ marginBottom: "8px" }}>Use horizontal rules only; avoid vertical lines within the table body</li>
                    </ul>
                    <p className="pera-text tagp" style={{ fontStyle: "italic", color: "#000" }}>Body Text Formatting: 11 pt Times New Roman, Regular</p>

                    <h3 className="large-title tagh2" style={{ fontSize: "18px", marginTop: "30px" }}>B. Figures and Charts</h3>
                    <ul className="pera-text tagp" style={{ paddingLeft: "20px", marginBottom: "15px" }}>
                      <li style={{ marginBottom: "8px" }}>Number sequentially: Figure. 1, Figure. 2, Figure. 3</li>
                      <li style={{ marginBottom: "8px" }}>Place caption below the figure and next to the number (e.g. Figure 1, A bonded labourer)</li>
                      <li style={{ marginBottom: "8px" }}>Ensure sufficient resolution (minimum 100 DPI)</li>
                      <li style={{ marginBottom: "8px" }}>Label axes and include units where applicable</li>
                    </ul>
                    <p className="pera-text tagp" style={{ fontStyle: "italic", color: "#000" }}>Body Text Formatting: 11 pt Times New Roman, Regular</p>

                    <h3 className="large-title tagh2" style={{ fontSize: "18px", marginTop: "15px" }}>C. Images</h3>
                    <p className="pera-text tagp">Images follow the same numbering as Figures and Charts.</p>
                    <ul className="pera-text tagp" style={{ paddingLeft: "20px", marginBottom: "15px" }}>
                      <li style={{ marginBottom: "8px" }}>Provide clear images (100 DPI minimum) – ensure every image file carries the same ID such as Figure 1 or Figure 2 as you have used in the body of the article.</li>
                      <li style={{ marginBottom: "8px" }}>Include descriptive labels and source attribution</li>
                      <li style={{ marginBottom: "8px" }}>Ensure compliance with copyright and usage rights</li>
                    </ul>
                  </Content>

                  {/* ACADEMIC WRITING STANDARDS */}
                  <Content title="V. Academic Writing Standards">
                    <p className="pera-text tagp">
                      Manuscripts must be written in formal academic English. Authors are expected to maintain objectivity, logical structure, and terminological consistency throughout the article. Please use a good text editor such as MS Word and run a Spelling + Grammar check before submitting your article. Grammatical errors will slow down the publishing process.
                    </p>
                    <p className="pera-text tagp">Authors must ensure:</p>
                    <ul className="pera-text tagp" style={{ paddingLeft: "20px", marginBottom: "15px" }}>
                      <li style={{ marginBottom: "8px" }}>Correct grammar and punctuation</li>
                      <li style={{ marginBottom: "8px" }}>Clear sentence construction and paragraph organisation</li>
                      <li style={{ marginBottom: "8px" }}>Avoidance of informal, emotive, or unsupported language</li>
                      <li style={{ marginBottom: "8px" }}>Consistent use of technical terminology</li>
                      <li style={{ marginBottom: "8px" }}>Logical and coherent argumentation</li>
                    </ul>
                  </Content>

                  {/* PLAGIARISM */}
                  <Content title="VI. Plagiarism and Originality Requirements">
                    <p className="pera-text tagp">
                      Academic integrity is central to research publishing. Submitted manuscripts must be original contributions that have not been published elsewhere and are not currently under review at another journal or conference. Every article will follow our own Similarity Index checking process. All reports will be made available on Author Dashboards.
                    </p>
                    <p className="pera-text tagp">Authors must:</p>
                    <ul className="pera-text tagp" style={{ paddingLeft: "20px", marginBottom: "15px" }}>
                      <li style={{ marginBottom: "8px" }}>Properly attribute all borrowed ideas, data, and expressions</li>
                      <li style={{ marginBottom: "8px" }}>Avoid self-plagiarism (reuse of own previously published material without attribution)</li>
                      <li style={{ marginBottom: "8px" }}>Maintain similarity levels within the threshold specified by the receiving institution or journal</li>
                      <li style={{ marginBottom: "8px" }}>Submit a plagiarism report where required</li>
                    </ul>
                  </Content>

                  {/* QUICK REFERENCE TABLE */}
                  <Content title="VII. Standard Article Structure — Quick Reference">
                    <p className="pera-text tagp">
                      The following sequence represents the prescribed order of sections for a standard research article:
                    </p>
                    <div className="table-responsive mb-3">
                      <table className="table table-bordered" style={{ fontSize: "14px" }}>
                        <thead style={{ backgroundColor: "#f0f8ff" }}>
                          <tr>
                            <th>No.</th>
                            <th>Section</th>
                            <th>Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr><td>1</td><td>Title</td><td>Mandatory</td></tr>
                          <tr><td>2</td><td>Author Details and Affiliations</td><td>Mandatory</td></tr>
                          <tr><td>3</td><td>Abstract</td><td>Mandatory</td></tr>
                          <tr><td>4</td><td>Keywords (Index Terms)</td><td>Mandatory</td></tr>
                          <tr><td>5</td><td>Introduction</td><td>Mandatory</td></tr>
                          <tr><td>6</td><td>Review of Literature</td><td>Optional</td></tr>
                          <tr><td>7</td><td>Research Methodology</td><td>Mandatory</td></tr>
                          <tr><td>8</td><td>Results: Data Analysis and Interpretation</td><td>Mandatory</td></tr>
                          <tr><td>9</td><td>Results: Findings of the Study</td><td>Mandatory</td></tr>
                          <tr><td>10</td><td>Discussion</td><td>Mandatory</td></tr>
                          <tr><td>11</td><td>Conclusion</td><td>Mandatory</td></tr>
                          <tr><td>12</td><td>Recommendations</td><td>Optional</td></tr>
                          <tr><td>13</td><td>Limitations and Future Scope</td><td>Optional</td></tr>
                          <tr><td>14</td><td>References / Bibliography</td><td>Mandatory</td></tr>
                          <tr><td>15</td><td>Appendices</td><td>Optional</td></tr>
                          <tr><td>16</td><td>Acknowledgements</td><td>Optional</td></tr>
                        </tbody>
                      </table>
                    </div>
                  </Content>

                  {/* PRE-SUBMISSION CHECKLIST */}
                  <Content title="VIII. Pre-Submission Checklist">
                    <p className="pera-text tagp">
                      Before submitting the manuscript, authors must verify each of the following:
                    </p>
                    <div style={{ backgroundColor: "#f9f9f9", padding: "20px", borderRadius: "8px", border: "1px solid #eee", marginBottom: "20px" }}>
                      <ul className="pera-text tagp" style={{ paddingLeft: "20px" }}>
                        <li style={{ marginBottom: "10px" }}>The manuscript follows the prescribed format and section order</li>
                        <li style={{ marginBottom: "10px" }}>All mandatory sections are complete and coherent</li>
                        <li style={{ marginBottom: "10px" }}>The title does not exceed 20 words and is accurately descriptive</li>
                        <li style={{ marginBottom: "10px" }}>The abstract is between 150 and 400 words and self-contained</li>
                        <li style={{ marginBottom: "10px" }}>Four to six keywords are provided</li>
                        <li style={{ marginBottom: "10px" }}>Citations and references are consistent with the required style</li>
                        <li style={{ marginBottom: "10px" }}>All tables and figures are sequentially numbered and captioned</li>
                        <li style={{ marginBottom: "10px" }}>Grammar, punctuation, and academic language have been reviewed (mandatory to run a Spelling and Grammar check before submission)</li>
                        <li style={{ marginBottom: "10px" }}>A plagiarism check has been conducted and the report is available (not to be submitted to us)</li>
                        <li style={{ marginBottom: "10px" }}>Research ethics requirements have been satisfied</li>
                        <li style={{ marginBottom: "10px" }}>All co-authors have reviewed and approved the final manuscript</li>
                        <li style={{ marginBottom: "10px" }}>Author details, affiliations, and correspondence information are complete</li>
                      </ul>
                    </div>
                  </Content>

                  {/* CONCLUDING OBSERVATION */}
                  <Content title="Concluding Observation">
                    <p className="pera-text tagp">
                      The structure of a research article is not merely a formatting requirement. It represents the intellectual architecture of scholarly communication. A properly structured and formatted manuscript enables researchers, reviewers, editors, and academic institutions to evaluate the quality, rigour, and contribution of the work systematically. Authors are therefore encouraged to treat structure and format not as administrative obligations, but as critical components of effective and responsible academic publishing.
                    </p>
                  </Content>

                </div>
              </div>

              {/* AGREEMENT SECTION */}
              <div className="agreement-section mt-5 mb-5" style={{ backgroundColor: "#f9f9f9", padding: "25px", borderRadius: "8px", border: "1px solid #ddd" }}>
                <div className="d-flex align-items-center mb-3">
                  <input
                    type="checkbox"
                    id="agreeGuidelinesPage"
                    checked={isMegaGuidelineAgreed}
                    onChange={(e) => setIsMegaGuidelineAgreed(e.target.checked)}
                    style={{ width: "20px", height: "20px", cursor: "pointer", marginRight: "10px", flexShrink: 0 }}
                  />
                  <label htmlFor="agreeGuidelinesPage" style={{ cursor: "pointer", marginBottom: 0, fontWeight: "500", fontSize: "16px" }}>
                    I have read and agree to the formatting guidelines
                  </label>
                </div>

                <div className="w-100 d-flex align-items-center mt-4" style={{ gap: "15px" }}>
                  <button
                    className="btn text-white"
                    style={{
                      backgroundColor: "#366c91",
                      borderColor: "#366c91",
                      padding: "10px 40px",
                      fontWeight: "600",
                      fontSize: "15px",
                      borderRadius: "6px",
                      whiteSpace: "nowrap",
                      flexShrink: 0,
                      minWidth: "160px",
                      height: "45px"
                    }}
                    onClick={handleContinue}
                  >
                    Continue
                  </button>

                  <button
                    className="btn text-white"
                    style={{
                      backgroundColor: "#2c5775",
                      borderColor: "#2c5775",
                      padding: "10px 40px",
                      fontWeight: "600",
                      fontSize: "15px",
                      borderRadius: "6px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "8px",
                      whiteSpace: "nowrap",
                      flexShrink: 0,
                      minWidth: "160px",
                      height: "45px"
                    }}
                    onClick={handleDownloadPdf}
                    disabled={isDownloading}
                  >
                    {isDownloading ? (
                      <>Downloading...</>
                    ) : (
                      <>
                        <i className="fa fa-download"></i> Download PDF
                      </>
                    )}
                  </button>
                </div>
              </div>

            </div>
          </div>
        </main>
        <AakashgangaFooter />
      </div>
    </>
  );
}

export default MegaJournalFormattingGuideline;
